import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, type VerificationLogEntry } from '@/lib/adminApi';
import { DataTable, Spinner, EmptyState, type Column } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';

const columns: Array<Column<VerificationLogEntry>> = [
  {
    key: 'subject',
    header: 'Subject',
    cell: (r) =>
      r.subject_type === 'driver' && r.subject_id ? (
        <Link to={`/drivers/${r.subject_id}`} className="metric" style={{ color: 'var(--accent, #2563eb)' }}>
          driver #{r.subject_id}
        </Link>
      ) : (
        <span className="metric">
          {r.subject_type || '—'} #{r.subject_id || '—'}
        </span>
      ),
  },
  {
    key: 'matched',
    header: 'Result',
    cell: (r) => (
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          padding: '2px 8px',
          borderRadius: 6,
          color: r.matched ? '#16a34a' : '#dc2626',
          background: r.matched ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
        }}
      >
        {r.matched ? 'pass' : 'fail'}
      </span>
    ),
  },
  {
    key: 'score',
    header: 'Match score',
    cell: (r) => <span className="text-xs">{r.score == null ? '—' : `${(r.score * 100).toFixed(1)}%`}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    cell: (r) => <span className="text-xs text-muted">{r.status || '—'}</span>,
  },
  {
    key: 'consent',
    header: 'Biometric consent',
    cell: (r) =>
      r.biometric_consent_at ? (
        <span className="text-xs text-muted">
          {r.biometric_consent_version || 'yes'} · {dateTime(r.biometric_consent_at)}
        </span>
      ) : (
        <span className="text-xs" style={{ color: 'var(--warn, #d97706)' }}>none</span>
      ),
  },
  {
    key: 'created_at',
    header: 'When',
    align: 'right',
    cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span>,
  },
];

/** ID Verification log ("God View") — every driver/applicant selfie face-match
 *  attempt verification-svc has run: who, when, pass/fail, similarity score, and
 *  the captured biometric consent. Read-only, auto-refreshes. Gated on
 *  drivers.read. An EMPTY log usually means no driver has hit the selfie gate yet
 *  (or verification-svc is in sandbox mode, which auto-passes every selfie). */
export function VerificationLog() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'verification-log'],
    queryFn: ({ signal }) => adminApi.verificationLog(200, undefined, signal),
    refetchInterval: 8000,
  });

  const items = data?.items ?? [];
  const passed = items.filter((r) => r.matched).length;

  return (
    <AdminPage>
      <AdminPageHeader
        title="ID Verification"
        subtitle={
          items.length
            ? `Last ${items.length} selfie checks · ${passed} passed (auto-refreshes)`
            : 'Driver selfie face-match attempts + results (auto-refreshes)'
        }
      />
      {isLoading ? (
        <Spinner label="Loading verification log…" />
      ) : isError ? (
        <EmptyState title="Couldn't load verification log" description={(error as Error)?.message} />
      ) : (
        <DataTable<VerificationLogEntry>
          columns={columns}
          rows={items}
          rowKey={(r) => String(r.id)}
          stickyHeader
          empty={
            <EmptyState
              title="No verifications recorded"
              description="No driver has completed a selfie face-match yet. If you expect results here, the selfie gate may not be firing on go-online, or verification-svc is in sandbox mode (auto-passes without a real match) — set VERIFICATION_MODE=live to enable real face matching."
            />
          }
        />
      )}
    </AdminPage>
  );
}
